import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const Navbar = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navItems = [
    { label: "ホーム", path: "/", icon: "ri-home-4-line" },
    { label: "マップ", path: "/map", icon: "ri-map-pin-2-line" },
    { label: "女の子一覧", path: "/characters", icon: "ri-heart-3-line" },
  ];

  const isActive = (path: string) => location.pathname === path;

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Scroll detection for navbar background
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || menuOpen
            ? "bg-white/95 backdrop-blur-md border-b border-pink-100"
            : "bg-white/90 backdrop-blur-md border-b border-pink-100"
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer z-10">
            <img
              src="https://public.readdy.ai/ai/img_res/db459360-20e7-4f18-828f-b6f77daa6c55.png"
              alt="Girls' Map Logo"
              className="h-10 w-auto object-contain"
            />
            <span
              className="text-xl font-bold text-pink-500"
              style={{ fontFamily: "'Pacifico', cursive" }}
            >
              Girls&apos; Map
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  isActive(item.path)
                    ? "bg-pink-500 text-white"
                    : "text-gray-600 hover:bg-pink-50 hover:text-pink-500"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden relative z-[60] w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="メニュー"
          >
            <div className="relative w-6 h-5 flex flex-col justify-between">
              <span
                className={`block h-0.5 rounded-full transition-all duration-300 origin-center ${
                  menuOpen
                    ? "bg-pink-500 rotate-45 translate-y-[9px]"
                    : "bg-pink-500"
                }`}
              />
              <span
                className={`block h-0.5 rounded-full transition-all duration-300 ${
                  menuOpen ? "bg-pink-500 opacity-0 scale-x-0" : "bg-pink-500"
                }`}
              />
              <span
                className={`block h-0.5 rounded-full transition-all duration-300 origin-center ${
                  menuOpen
                    ? "bg-pink-500 -rotate-45 -translate-y-[9px]"
                    : "bg-pink-500"
                }`}
              />
            </div>
          </button>
        </div>
      </nav>

      {/* Mobile Full-screen Menu Overlay */}
      <div
        className={`fixed inset-0 z-[55] md:hidden transition-all duration-300 ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => setMenuOpen(false)}
        />

        {/* Menu Panel — slides in from right */}
        <div
          className={`absolute top-0 right-0 bottom-0 w-72 bg-white flex flex-col transition-transform duration-300 ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* Panel Header */}
          <div className="h-16 flex items-center px-6 border-b border-pink-100">
            <span
              className="text-lg font-bold text-pink-500"
              style={{ fontFamily: "'Pacifico', cursive" }}
            >
              Girls&apos; Map
            </span>
          </div>

          {/* Nav Links */}
          <div className="flex-1 px-4 py-6 flex flex-col gap-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-4 px-5 py-4 rounded-2xl text-base font-semibold transition-all cursor-pointer ${
                  isActive(item.path)
                    ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white"
                    : "text-gray-700 hover:bg-pink-50 hover:text-pink-500"
                }`}
              >
                <div
                  className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${
                    isActive(item.path) ? "bg-white/20" : "bg-pink-50"
                  }`}
                >
                  <i className={`${item.icon} text-lg ${isActive(item.path) ? "text-white" : "text-pink-400"}`}></i>
                </div>
                {item.label}
                {isActive(item.path) && (
                  <i className="ri-arrow-right-line ml-auto text-white/70"></i>
                )}
              </Link>
            ))}
          </div>

          {/* CTA at bottom */}
          <div className="px-4 pb-8">
            <Link
              to="/map"
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full py-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-full font-black text-sm cursor-pointer whitespace-nowrap"
              style={{ boxShadow: "0 4px 20px rgba(244,63,94,0.4)" }}
            >
              <i className="ri-crosshair-2-line text-base"></i>
              今すぐ近くの女の子を探す
            </Link>
            <p className="text-center text-xs text-gray-400 mt-3">登録不要・完全無料</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
